import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import {
    Mic,
    MicOff,
    FileText,
    Download,
    Copy,
    Loader2,
    X
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * TranscriptionPanel - Real-time meeting transcription display
 * Shows live transcript with speaker labels and timestamps
 */
const TranscriptionPanel = ({
    isTranscribing,
    isConnecting,
    transcript,
    onToggle,
    onClose,
    error
}) => {
    const scrollRef = useRef(null);

    // Auto-scroll to bottom when new transcript entries arrive
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [transcript]);

    // Copy transcript to clipboard
    const copyTranscript = () => {
        const text = transcript.map(u => `${u.speaker}: ${u.text}`).join('\n');
        navigator.clipboard.writeText(text);
        toast.success('Transcript copied to clipboard');
    };

    // Download transcript as text file
    const downloadTranscript = () => {
        const text = transcript.map(u => `[${formatTime(u.timestamp)}] ${u.speaker}: ${u.text}`).join('\n');
        const blob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript-${new Date().toISOString().split('T')[0]}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Transcript downloaded');
    };

    // Format timestamp
    const formatTime = (seconds) => {
        if (!seconds) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 'auto', opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="w-full md:w-[350px] h-full max-h-[calc(100dvh-140px)] bg-void-paper border-l border-white/5 flex flex-col overflow-hidden z-20"
        >
            {/* Header */}
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-neon-cyan" />
                    <h3 className="font-heading font-semibold text-white">Transcription</h3>
                    {isTranscribing && (
                        <span className="flex items-center gap-1.5 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                            Live
                        </span>
                    )}
                </div>
                <button onClick={onClose} className="text-grey-400 hover:text-white text-xl">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Transcription Toggle */}
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
                <Button
                    onClick={onToggle}
                    disabled={isConnecting}
                    variant={isTranscribing ? "destructive" : "default"}
                    size="sm"
                    className={isTranscribing
                        ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        : "bg-neon-cyan text-black hover:bg-neon-cyan/80"
                    }
                >
                    {isConnecting ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Connecting...
                        </>
                    ) : isTranscribing ? (
                        <>
                            <MicOff className="w-4 h-4 mr-2" />
                            Stop Transcription
                        </>
                    ) : (
                        <>
                            <Mic className="w-4 h-4 mr-2" />
                            Start Transcription
                        </>
                    )}
                </Button>

                {transcript.length > 0 && (
                    <div className="flex gap-1">
                        <button
                            onClick={copyTranscript}
                            className="p-2 rounded-lg hover:bg-white/5 text-grey-400 hover:text-white transition-colors"
                            title="Copy transcript"
                        >
                            <Copy className="w-4 h-4" />
                        </button>
                        <button
                            onClick={downloadTranscript}
                            className="p-2 rounded-lg hover:bg-white/5 text-grey-400 hover:text-white transition-colors"
                            title="Download transcript"
                        >
                            <Download className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Error Display */}
            {error && (
                <div className="mx-3 mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm">{error}</p>
                </div>
            )}

            {/* Transcript Content */}
            <ScrollArea className="flex-1 p-4">
                {transcript.length === 0 ? (
                    <div className="text-center text-grey-400 py-8">
                        <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm mb-1">No transcript yet</p>
                        <p className="text-xs text-grey-500">
                            {isTranscribing
                                ? "Listening for speech..."
                                : "Click 'Start Transcription' to begin"
                            }
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {transcript.map((entry, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="group"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-neon-cyan/20 to-neon-purple/20 flex items-center justify-center">
                                        <span className="text-xs font-medium text-neon-cyan">
                                            {entry.speaker?.charAt(0) || 'S'}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-sm font-medium text-grey-200">
                                                {entry.speaker || 'Speaker'}
                                            </span>
                                            <span className="text-xs text-grey-500">
                                                {formatTime(entry.timestamp)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-grey-300 leading-relaxed">
                                            {entry.text}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                        <div ref={scrollRef} />
                    </div>
                )}
            </ScrollArea>

            {/* Footer Stats */}
            {transcript.length > 0 && (
                <div className="p-3 border-t border-white/5 text-xs text-grey-500 flex justify-between">
                    <span>{transcript.length} utterances</span>
                    <span>
                        {transcript.reduce((acc, u) => acc + (u.text?.split(' ').length || 0), 0)} words
                    </span>
                </div>
            )}
        </motion.div>
    );
};

export default TranscriptionPanel;
